import React, { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Disclosure, Popover, Transition } from '@headlessui/react';

import { ChevronDownIcon, MenuIcon, XIcon } from '@heroicons/react/outline';

import ExchangeLogoColor from 'img/exchange.logo_color.png';
import LogoColor from 'img/logo_color.png';

import { classNames } from 'utils/functions';
import StorageService from 'utils/storage';
import { useAuth } from 'contexts/Auth';

export default function InternalHeader() {
  const location = useLocation();
  const history = useNavigate();
  const auth = useAuth();

  const userLevel = StorageService.get('userData')?.level;

  const navigation = [
    { name: 'Mis Operaciones', href: '/my_operations' },
    ...(userLevel === '31' ? [] : [{ name: 'Cuentas Bancarias', href: '/bank_accounts' }]),
    { name: 'Reclamo', href: '/claim' },
    { name: 'Mi Perfil', href: '/profile' },
  ];

  const secondaryNavigation = [
    { name: 'Operaciones', href: '/company_operations' },
    { name: 'Usuarios', href: '/users' },
    { name: 'Cuentas Bancarias', href: '/bank_accounts' },
  ];

  return (
    <Disclosure as="nav" className="fixed z-20 w-full bg-white sm:relative">
      {({ open }) => (
        <>
          <div className="relative flex items-center justify-between bg-white px-4 pb-1 shadow-md sm:items-stretch sm:px-12 sm:pb-0">
            <Link to="/">
              <img
                src={process.env.REACT_APP_LOGO === 'exchange' ? ExchangeLogoColor : LogoColor}
                className="my-4 h-20"
                alt=""
              />
            </Link>
            <div className="hidden items-center space-x-6 sm:flex">
              {userLevel === '31' && (
                <Popover className="relative z-0 h-full">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className={classNames(
                          open ||
                            ['/company_operations', '/users', '/bank_accounts'].includes(
                              location.pathname
                            )
                            ? 'border-b-8'
                            : 'hover:border-b-8',
                          'flex h-full cursor-pointer items-center justify-center border-blue-900 px-4 text-center transition-all duration-200'
                        )}
                      >
                        <span>Gestión de la empresa</span>
                        <ChevronDownIcon
                          className={classNames(
                            open ? 'text-gray-600' : 'text-gray-400',
                            'ml-2 h-5 w-5 group-hover:text-gray-500'
                          )}
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 -translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 -translate-y-1"
                      >
                        <Popover.Panel className="absolute inset-x-0 z-10 transform bg-white shadow-lg">
                          {secondaryNavigation.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={classNames(
                                location.pathname === item.href
                                  ? 'border-b-4 font-bold'
                                  : 'hover:border-b-8',
                                'flex h-full cursor-pointer flex-col items-center justify-center border-blue-800 py-4 px-4 text-center transition-all duration-200'
                              )}
                            >
                              <p className="text-lg">{item.name}</p>
                            </Link>
                          ))}
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
              )}
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classNames(
                    location.pathname === item.href ? 'border-b-8 font-bold' : 'hover:border-b-8',
                    'ml-6 flex h-full cursor-pointer flex-col items-center justify-center border-blue-900 px-4 transition-all duration-200'
                  )}
                >
                  <p className="text-lg">{item.name}</p>
                </Link>
              ))}
              <button
                onClick={() => auth.signout(() => history('/', { replace: true }))}
                className="ml-20 rounded-lg bg-primary-600 px-12 py-2 text-white transition-colors hover:bg-primary-700"
              >
                Salir
              </button>
            </div>
            <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white sm:hidden">
              <span className="sr-only">Open main menu</span>
              {open ? (
                <XIcon className="block h-8 w-8" aria-hidden="true" />
              ) : (
                <MenuIcon className="block h-8 w-8" aria-hidden="true" />
              )}
            </Disclosure.Button>
          </div>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel className="sm:hidden">
              <div className="space-y-1 px-2 pt-2 pb-3 shadow-lg">
                {userLevel === '31' &&
                  secondaryNavigation.map((item) => (
                    <Disclosure.Button
                      key={item.name}
                      as={Link}
                      to={item.href}
                      className={classNames(
                        location.pathname === item.href
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-700 hover:bg-primary-600 hover:text-white',
                        'block rounded-md px-3 py-2 text-base font-medium'
                      )}
                      aria-current={location.pathname === item.href ? 'page' : undefined}
                    >
                      {item.name}
                    </Disclosure.Button>
                  ))}
                {navigation.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className={classNames(
                      location.pathname === item.href
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-700 hover:bg-primary-600 hover:text-white',
                      'block rounded-md px-3 py-2 text-base font-medium'
                    )}
                    aria-current={location.pathname === item.href ? 'page' : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
